import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import type { SitrepSectionConflictDetails } from 'components/DisasterGPT/hooks/useSitrepsV2';

type SitrepSectionConflictModalProps = {
  show: boolean;
  conflict: SitrepSectionConflictDetails | null;
  draftContent: string;
  onKeepExisting: () => void;
  onOverride: (latestVersionId: string, editedContent: string) => void;
  isOverriding?: boolean;
  isDraftReadOnly?: boolean;
};

const formatTimestamp = (isoTimestamp: string): string => {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return isoTimestamp;
  return date.toLocaleString();
};

const SitrepSectionConflictModal: React.FC<SitrepSectionConflictModalProps> = ({
  show,
  conflict,
  draftContent,
  onKeepExisting,
  onOverride,
  isOverriding = false,
  isDraftReadOnly = false,
}) => {
  const [editedContent, setEditedContent] = useState(draftContent);

  useEffect(() => {
    if (show) {
      setEditedContent(draftContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, conflict?.latestVersionId]);

  if (!conflict) return null;

  const otherUserName = conflict.latestUpdatedByUserName ?? 'another user';
  const updatedAt = formatTimestamp(conflict.latestVersionTimestamp);
  const latestText = conflict.latestVersionText ?? '';

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) onKeepExisting();
      }}
    >
      <DialogContent className="tw:sm:max-w-6xl tw:max-h-[85vh] tw:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>This section was edited by another user</DialogTitle>
          <DialogDescription>
            {otherUserName} saved changes
            {conflict.sectionName ? ` to ${conflict.sectionName}` : ''} since
            you started editing. <br />
            Review both versions below and choose whether to override with your
            edits or keep their content.
          </DialogDescription>
        </DialogHeader>
        <div className="row g-3">
          <div className="col-md-6 col-12">
            <div className="d-flex flex-column h-100">
              <p className="d-flex align-items-center mb-2">
                <span className={`status-dot danger`} />
                Current content from {otherUserName} at {updatedAt}
              </p>
              <div className="flex-grow-1 rounded border p-2">
                {latestText ? (
                  <TiptapRichTextEditor
                    value={latestText}
                    readOnly
                    showToolbar={false}
                    normalizeMarkdown
                  />
                ) : (
                  <em className="text-muted">No content.</em>
                )}
              </div>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isOverriding}
                  onClick={onKeepExisting}
                >
                  Keep current content
                </Button>
              </div>
            </div>
          </div>
          <div className="col-md-6 col-12">
            <div className="d-flex flex-column h-100">
              <p className="d-flex align-items-center mb-2">
                <span className={`status-dot success`} />
                Your edits
              </p>
              <div className="flex-grow-1 rounded border p-2">
                {isDraftReadOnly ? (
                  <TiptapRichTextEditor
                    value={draftContent}
                    readOnly
                    showToolbar={false}
                    className="w-100"
                    normalizeMarkdown
                  />
                ) : (
                  <TiptapRichTextEditor
                    value={editedContent}
                    onChange={(html) => setEditedContent(html)}
                    placeholder="Enter section content..."
                    disabled={isOverriding}
                    minHeight="150px"
                    className="w-100"
                    normalizeMarkdown
                  />
                )}
              </div>
              <div className="mt-2">
                <Button
                  type="button"
                  disabled={isOverriding}
                  onClick={() =>
                    onOverride(conflict.latestVersionId, editedContent)
                  }
                >
                  {isOverriding ? 'Overriding…' : 'Override with my edits'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SitrepSectionConflictModal;
